import { useState, useEffect } from "react";
import { CartHeader } from "./CartHeader";
import { CartItem } from "./CartItem";
// import { CouponSection } from "./CouponSection";
import { DeliveryAddressSection } from "./DeliveryAddress/DeliveryAddressSection";
import { PriceSummary } from "./PriceSummary";
import { CheckoutButton } from "./CheckoutButton";
import { EmptyCart } from "./EmptyCart";
import { useCart } from "@nusrif/contexts";
import { useNavigate } from "react-router";
import { setShippingAddress } from "@nusrif/services";
import { trackEvent } from "@nusrif/services";
import { DeliveryAddress, ApiCartLine } from "@nusrif/types";
import { useNotificationHelpers } from "@nusrif/hooks";

interface AppliedCoupon {
  code: string;
  discount: number;
  type: "percentage" | "fixed";
  description: string;
}

export const MyCartPage = () => {
  const { state, updateQuantity, removeItem, refreshCart } = useCart();
  const navigate = useNavigate();
  const { showError } = useNotificationHelpers();

  const [appliedCoupon] = useState<
    AppliedCoupon | undefined
  >();
  // const [appliedCoupon, setAppliedCoupon] = useState<
  //   AppliedCoupon | undefined
  // >();

  const [deliveryAddress, setDeliveryAddress] = useState<
    DeliveryAddress | undefined
  >();

  const [updatingItemIds, setUpdatingItemIds] = useState<string[]>([]);

  // // Mock coupon database
  // const mockCoupons: Record<string, AppliedCoupon> = {
  //   SAVE10: {
  //     code: "SAVE10",
  //     discount: 10,
  //     type: "percentage",
  //     description: "Get 10% off on your order",
  //   },
  //   FIRST20: {
  //     code: "FIRST20",
  //     discount: 20,
  //     type: "percentage",
  //     description: "First time user discount",
  //   },
  //   WELCOME15: {
  //     code: "WELCOME15",
  //     discount: 15,
  //     type: "percentage",
  //     description: "Welcome discount for new users",
  //   },
  //   FLAT100: {
  //     code: "FLAT100",
  //     discount: 100,
  //     type: "fixed",
  //     description: "Flat ₹100 off on orders above ₹500",
  //   },
  // };

  const handleQuantityChange = async (itemId: string, quantity: number) => {
    setUpdatingItemIds((ids) => [...ids, itemId]);
    const result = await updateQuantity(itemId, quantity);
    if (!result.success) {
      showError(
        "Failed to update quantity",
        result.error || "Please try again."
      );
    }
    setUpdatingItemIds((ids) => ids.filter((id) => id !== itemId));
  };

  const handleRemoveItem = (itemId: string) => {
    removeItem(itemId);
  };

  // const handleApplyCoupon = async (
  //   code: string
  // ): Promise<AppliedCoupon | null> => {
  //   // Simulate API call
  //   await new Promise((resolve) => setTimeout(resolve, 1000));
  //   const coupon = mockCoupons[code];
  //   if (coupon) {
  //     // Check if coupon is applicable
  //     if (code === "FLAT100" && (state.cart?.subTotalWithTax || 0) < 500) {
  //       throw new Error("Minimum order value ₹500 required for this coupon");
  //     }
  //     setAppliedCoupon(coupon);
  //     return coupon;
  //   }
  //   return null;
  // };

  // const handleRemoveCoupon = () => {
  //   setAppliedCoupon(undefined);
  // };

  const handleAddressChange = async (address: DeliveryAddress) => {
    setDeliveryAddress(address);
    await setShippingAddress({
      streetLine1: address.addressLine1,
      streetLine2: address.addressLine2,
      city: address.city,
      province: address.state,
      postalCode: address.pincode,
      fullName: address.name,
      phoneNumber: address.phone,
      countryCode: "IN",
      customFields: {
        addressType: address.type,
      },
    });
    await refreshCart();
    // Here you would typically save to localStorage or API
    localStorage.setItem("deliveryAddress", JSON.stringify(address));
  };

  const handleCheckout = () => {
    if (!deliveryAddress) {
      alert("Please add a delivery address to proceed");
      return;
    }

    // Track checkout initiation
    trackEvent({
      event: "checkout_initiated",
      properties: {
        cart_id: state.cart?.id || "",
        cart_total: state.cart?.totalWithTax || 0,
        cart_subtotal: state.cart?.subTotalWithTax || 0,
        delivery_fee: state.cart?.shippingWithTax || 0,
        item_count:
          state.cart?.lines.reduce((sum, line) => sum + line.quantity, 0) || 0,
        unique_items: state.cart?.lines.length || 0,
        has_coupon: !!appliedCoupon,
        coupon_code: appliedCoupon?.code || "",
        coupon_discount: appliedCoupon?.discount || 0,
        coupon_type: appliedCoupon?.type || "",
        address_type: deliveryAddress.type || "other",
        address_city: deliveryAddress.city,
        address_state: deliveryAddress.state,
        currency: state.cart?.currencyCode || "INR",
      },
    });

    // Save coupon to localStorage for payment page
    if (appliedCoupon) {
      localStorage.setItem("appliedCoupon", JSON.stringify(appliedCoupon));
    }
    console.log("Proceeding to checkout with:", {
      items: state.cart?.lines || [],
      cart: state.cart,
      coupon: appliedCoupon,
      address: deliveryAddress,
    });
    // Navigate to payment page
    navigate("/payment");
  };

  // Load saved address on mount
  useEffect(() => {
    const savedAddress = state.cart?.shippingAddress;
    if (savedAddress && savedAddress.fullName) {
      try {
        setDeliveryAddress({
          name: savedAddress.fullName || "",
          phone: savedAddress.phoneNumber || "",
          addressLine1: savedAddress.streetLine1 || "",
          addressLine2: savedAddress.streetLine2 || "",
          city: savedAddress.city || "",
          state: savedAddress.province || "",
          pincode: savedAddress.postalCode || "",
          id: "",
        });
      } catch (error) {
        console.error("Error loading saved address:", error);
      }
    }
  }, [state.cart]);

  // Calculate cart summary from Cart type
  const cartSummary = state.cart
    ? {
        subTotal: state.cart.subTotal,
        subTotalWithTax: state.cart.subTotalWithTax,
        total: state.cart.total,
        totalWithTax: state.cart.totalWithTax,
        shipping: state.cart.shipping,
        shippingWithTax: state.cart.shippingWithTax,
        itemCount: state.cart.lines.reduce(
          (sum, line) => sum + line.quantity,
          0
        ),
      }
    : {
        subTotal: 0,
        subTotalWithTax: 0,
        total: 0,
        totalWithTax: 0,
        shipping: 0,
        shippingWithTax: 0,
        itemCount: 0,
      };

  return (
    <div className="min-h-screen w-full bg-gray-50">
      {/* Fixed Header */}
      <CartHeader itemCount={cartSummary.itemCount} />
      {/* Main Content */}
      <div className="pt-20 pb-32">
        {!state.cart || state.cart.lines.length === 0 ? (
          <EmptyCart />
        ) : (
          <div className="px-5 space-y-6">
            {/* Cart Items List */}
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-black">Cart Items</h2>
              {state.cart.lines.map((line: ApiCartLine) => (
                <CartItem
                  key={line.id}
                  item={{
                    id: line.id,
                    productId: line.productVariant.product.id,
                    name: line.productVariant.product.name,
                    subtitle: line.productVariant.name,
                    price: line.unitPriceWithTax,
                    mrpPrice: line.productVariant.product.customFields?.mrpPrice,
                    quantity: line.quantity,
                    image: line.featuredAsset?.preview || "/image.png",
                    productVariantId: line.productVariant.id,
                  }}
                  onQuantityChange={handleQuantityChange}
                  onRemove={handleRemoveItem}
                  updating={updatingItemIds.includes(line.id)}
                />
              ))}
            </div>
            {/* Coupon Section */}
            {/* <CouponSection
              appliedCoupon={appliedCoupon}
              onApplyCoupon={handleApplyCoupon}
              onRemoveCoupon={handleRemoveCoupon}
            /> */}
            {/* Delivery Address Section */}
            <DeliveryAddressSection
              currentAddress={deliveryAddress}
              onAddressChange={handleAddressChange}
            />
            {/* Price Summary Section */}
            <PriceSummary
              summary={cartSummary}
              appliedCoupon={appliedCoupon}
              deliveryAddress={deliveryAddress}
            />
          </div>
        )}
      </div>
      {/* Fixed Checkout Button */}
      {state.cart && state.cart.lines.length > 0 && (
        <CheckoutButton
          total={cartSummary.total}
          itemCount={cartSummary.itemCount}
          onCheckout={handleCheckout}
          disabled={!deliveryAddress}
        />
      )}
    </div>
  );
};
